import { useRouter } from 'next/navigation'
import { Icon, SEO, Alert } from 'components'
import { useQuery } from '@tanstack/react-query'
import { GetStaticPaths, GetStaticProps } from 'next'

import api from 'lib/api'
import { useStore } from 'lib/store'
import { formatTextRich } from 'utils/format'
import { getVacancyApplied } from 'services/vacancy'
import { VacancyContent, CandidatureButton } from './components'
import { buildVacancyToGoogleForJobs, buildVacancyToPage } from 'utils/vacancy'

const VacancyPage = props => {
  const router = useRouter()
  const company = props?.company
  const user = useStore(store => store.user)

  const vacancy = buildVacancyToPage(props.vacancy)
  const googleForJobs = buildVacancyToGoogleForJobs(props.vacancy)

  const verifyRequiredFields =
    !!vacancy.requiredFields.length ||
    !!vacancy.language.length ||
    !!vacancy.hardSkills.length

  const { data: isApplied } = useQuery({
    queryKey: ['vacancy-applied', vacancy.id],
    queryFn: () => getVacancyApplied(vacancy.id),
    enabled: !!vacancy.id && !!user
  })

  const isDisableVacancy = vacancy.currentState !== 'em_andamento'
  const seoDescription = formatTextRich(vacancy.description)
    .join('. ')
    .slice(0, 120)

  return (
    <>
      <SEO
        tabName={`Vaga ${vacancy.title}`}
        title={`Veja os detalhes da vaga ${vacancy.title} na empresa ${company.name}`}
        description={seoDescription}
        keywords={`${vacancy.title}, Vaga de Emprego, empregos, vagas na ${company.name}, trabalho na ${company.name}, carreiras, oportunidades, oportunidades de trabalho, recrutamento`}
        image={company.logo}
        googleForJobs={googleForJobs}
        noIndex={isDisableVacancy}
      />

      <section className="w-full flex bg-white relative rm:sticky top-0 left-0 shadow-sm px-4 xl:px-0">
        <div className="max-w-7xl mx-auto w-full flex items-center gap-3 py-4">
          <button type="button" onClick={() => router.back()}>
            <Icon face="arrow_back_ios" size="md" />
          </button>

          <div className="flex flex-col flex-1">
            <h1 className="text-xs sm:text-sm font-semibold leading-5">
              {vacancy.title}
            </h1>
            <strong className="font-medium text-gray-500 text-body2">
              {company.name}
            </strong>
          </div>

          {!isDisableVacancy && (
            <div className="ml-auto fixed sm:relative bottom-0 right-0 w-full sm:w-auto bg-white p-4 sm:p-0 shadow-lg sm:shadow-none">
              {isApplied ? (
                <div className="flex items-center justify-center gap-1.5 rounded-lg px-6 bg-success-500/10 h-10 text-success-700">
                  <Icon face="task_alt" />
                  <span className="text-body2">Candidatura já realizada!</span>
                </div>
              ) : (
                <CandidatureButton
                  {...vacancy}
                  verifyRequiredFields={verifyRequiredFields}
                />
              )}
            </div>
          )}
        </div>
      </section>

      <Alert
        color="warning"
        isOpen={isDisableVacancy}
        className="max-w-7xl mx-auto mt-5 shadow-sm"
      >
        Esta vaga não aceita mais candidaturas!
      </Alert>

      <VacancyContent {...vacancy} />
    </>
  )
}
export const getStaticPaths: GetStaticPaths = () => {
  return { paths: [], fallback: 'blocking' }
}

export const getStaticProps: GetStaticProps = async context => {
  const id = context.params.id

  try {
    const { data: vacancy } = await api(`/home/vacancy/${id}`)
    return { props: { vacancy }, revalidate: 60 * 3 }
  } catch (err) {
    console.log(err)
  }

  return { notFound: true }
}

export default VacancyPage
